import { Database, HelpCircle, Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BriefBanner } from '../components/BriefBanner.js';
import { SourceStrip, TopThreatsCard } from '../components/CommandCenter.js';
import { EventDrawer } from '../components/EventDrawer.js';
import { FilterBar, type Filters, emptyFilters } from '../components/FilterBar.js';
import { MapView } from '../components/Map.js';
import { SEVERITY_COLORS, SEVERITY_ICONS } from '../components/SeverityPill.js';
import { WelcomeModal, welcomeForced, welcomeWasDismissed } from '../components/WelcomeModal.js';
import { ZoneDrawer } from '../components/ZoneDrawer.js';
import { Button } from '../components/ui/Button.js';
import { useEvents, useSeed, useSeedPresets, useStats, useZones } from '../hooks/useData.js';
import { timeAgo } from '../lib/format.js';

export function Dashboard() {
  const stats = useStats();
  const zones = useZones();
  const since = Math.floor(Date.now() / 1000) - 14 * 86400;
  const events = useEvents({ since, limit: 1000 });
  const seed = useSeed();
  const presets = useSeedPresets();

  const [filters, setFilters] = useState<Filters>(emptyFilters());
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [eventId, setEventId] = useState<number | null>(null);
  const [showWelcome, setShowWelcome] = useState(() => welcomeForced() || !welcomeWasDismissed());

  // Only one right-hand drawer at a time — opening one closes the other.
  const openZone = (id: number) => {
    setEventId(null);
    setZoneId(id);
  };
  const openEvent = (id: number) => {
    setZoneId(null);
    setEventId(id);
  };

  // Load a preset and reflect it in the URL so the view is a shareable
  // deeplink (?seed=<preset>).
  const loadSeed = (key: string) => {
    seed.mutate(key);
    const u = new URL(window.location.href);
    u.searchParams.set('seed', key);
    window.history.replaceState({}, '', u);
  };

  // Bootstrap: a ?seed=<preset> deeplink always loads that dataset on
  // arrival; otherwise auto-load the default preset if the map is empty,
  // so a first-time visitor never lands on a blank map.
  const bootstrapped = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot bootstrap
  useEffect(() => {
    if (bootstrapped.current || !zones.isFetched) return;
    const urlSeed = new URLSearchParams(window.location.search).get('seed');
    if (urlSeed) {
      bootstrapped.current = true;
      loadSeed(urlSeed);
      return;
    }
    const isEmpty = (zones.data?.length ?? 0) === 0;
    if (isEmpty && presets.data?.length) {
      bootstrapped.current = true;
      loadSeed(presets.data[0]!.key);
    }
  }, [zones.isFetched, zones.data, presets.data]);

  const filteredEvents = useMemo(
    () =>
      (events.data ?? []).filter(
        (e) => filters.categories.has(e.category) && filters.severities.has(e.severity),
      ),
    [events.data, filters],
  );
  const filteredZones = useMemo(
    () => (zones.data ?? []).filter((z) => !z.maxSeverity || filters.severities.has(z.maxSeverity)),
    [zones.data, filters],
  );

  const lastUpdated = stats.data
    ? Math.max(0, ...Object.values(stats.data.lastUpdated).filter((v): v is number => v != null))
    : 0;

  const empty = (zones.data?.length ?? 0) === 0;

  return (
    <div className="flex h-full flex-col">
      <div className="z-10 flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-4 py-2">
        <span className="text-sm font-semibold tracking-tight">OpenSentry</span>
        <span className="rounded bg-[var(--color-panel-2)] px-2 py-0.5 text-xs text-[var(--color-muted)]">
          Demo Workspace
        </span>
        <span className="mono text-[11px] text-[var(--color-muted)]">
          {lastUpdated ? `updated ${timeAgo(lastUpdated)}` : 'no ingest yet'}
        </span>
        <button
          type="button"
          onClick={() => setShowWelcome(true)}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-[var(--color-muted)] hover:bg-[var(--color-panel-2)] hover:text-[var(--color-text)]"
          aria-label="About OpenSentry"
        >
          <HelpCircle size={13} /> About
        </button>
        <div className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] bg-[var(--color-panel-2)] pl-2 text-[11px]">
          <Database size={12} className="text-[var(--color-muted)]" />
          <select
            aria-label="Load demo workforce"
            disabled={seed.isPending}
            value=""
            onChange={(e) => e.target.value && loadSeed(e.target.value)}
            className="bg-transparent py-1 pr-1 text-[11px] text-[var(--color-text)] outline-none disabled:opacity-50"
          >
            <option value="">{seed.isPending ? 'Loading…' : 'Load demo data…'}</option>
            {(presets.data ?? []).map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto">
          <FilterBar filters={filters} onChange={setFilters} />
        </div>
      </div>

      <BriefBanner />

      <div className="relative flex-1">
        <MapView
          zones={filteredZones}
          events={filteredEvents}
          onZoneClick={openZone}
          onEventClick={openEvent}
        />

        {!empty && (
          <div
            className="pointer-events-none absolute left-4 top-4 z-[5] flex w-72 flex-col gap-3"
            style={{ maxHeight: 'calc(100% - 6rem)' }}
          >
            {(() => {
              const allZones = zones.data ?? [];
              const peopleIn = (pred: (z: (typeof allZones)[number]) => boolean) =>
                allZones.filter(pred).reduce((sum, z) => sum + z.employeeCount, 0);
              const total = peopleIn(() => true);
              const exposed = peopleIn((z) => z.maxSeverity != null);
              return (
                <div className="pointer-events-auto w-full shrink-0 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)]/90 px-4 py-3 shadow-lg backdrop-blur">
                  <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">
                    Workforce exposure
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <span className="mono text-2xl font-semibold leading-none">{exposed}</span>
                    <span className="text-xs text-[var(--color-muted)]">
                      of {total} in a threatened zone
                    </span>
                  </div>
                  <div className="mt-2.5 space-y-1 text-[11px]">
                    {(['critical', 'high', 'medium', 'low'] as const).map((sev) => {
                      const n = peopleIn((z) => z.maxSeverity === sev);
                      const Icon = SEVERITY_ICONS[sev];
                      return (
                        <div key={sev} className="flex items-center gap-1.5">
                          <Icon size={12} style={{ color: SEVERITY_COLORS[sev] }} aria-hidden />
                          <span className="capitalize text-[var(--color-muted)]">{sev}</span>
                          <span className="mono ml-auto">{n}</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-1.5 border-t border-[var(--color-border)] pt-1">
                      <span className="h-3 w-3 rounded-full border border-[var(--color-border)] bg-[var(--color-text)]" />
                      <span className="text-[var(--color-muted)]">No active threat</span>
                      <span className="mono ml-auto">{peopleIn((z) => z.maxSeverity == null)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <TopThreatsCard events={filteredEvents} onEventClick={openEvent} />
          </div>
        )}

        {!empty && (
          <SourceStrip events={events.data ?? []} lastUpdated={stats.data?.lastUpdated ?? {}} />
        )}

        {empty && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-6 text-center">
              <p className="mb-3 text-sm text-[var(--color-muted)]">No workforce loaded yet.</p>
              <Button
                variant="primary"
                disabled={seed.isPending}
                onClick={() => loadSeed(presets.data?.[0]?.key ?? 'us-europe-japan')}
              >
                {seed.isPending ? 'Loading…' : 'Load demo data'}
              </Button>
            </div>
          </div>
        )}

        <Link
          to="/lookup"
          className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-panel)] px-4 py-2 text-sm shadow-lg hover:bg-[var(--color-panel-2)]"
        >
          <Search size={15} /> Lookup a location
        </Link>
      </div>

      <ZoneDrawer zoneId={zoneId} onClose={() => setZoneId(null)} />
      <EventDrawer eventId={eventId} onClose={() => setEventId(null)} />
      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} />}

      {seed.isPending && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] px-8 py-6 shadow-2xl">
            <Loader2 size={28} className="animate-spin text-[var(--color-sev-high)]" />
            <div className="text-sm font-semibold">Loading demo workforce…</div>
            <div className="text-xs text-[var(--color-muted)]">
              Geocoding people, building zones, joining threats
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
